import { StanProgressBar } from '@/components';
import { createIconElement } from '@/lib';
import IcUser from '@/assets/icons/ic-user-01.svg';
import IcBox from '@/assets/icons/ic-box.svg';
import { useImportProgress, useProductGrantProgress } from '../hooks/useImportProgress';

export interface ProgressBannerProps {
    icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
    title: string;
    progress: number;
}

export function ProgressBanner({ icon, title, progress }: ProgressBannerProps) {
    // NOTE: Backend seems to not return the progress, if progress is 0 or not provided, show animation instead
    const isIndeterminate = !progress || progress === 0;

    return (
        <div className='bg-brand-subtle mb-4 flex w-full flex-col gap-3 rounded-2xl p-5'>
            <div className='flex items-center gap-3'>
                <span className='text-heading-2 flex-shrink-0'>
                    {createIconElement(icon, { className: 'h-5 w-5' })}
                </span>
                <span className='text-heading-2'>{title}</span>
            </div>
            {isIndeterminate ? (
                // Indeterminate animated progress bar when no progress data
                <div className='bg-canvas relative h-2 w-full overflow-hidden rounded-full'>
                    <div className='bg-brand-main animate-indeterminate absolute h-full w-1/3 rounded-full' />
                </div>
            ) : (
                <StanProgressBar value={progress} min={0} max={100} barColor='var(--color-canvas)' />
            )}
        </div>
    );
}

export function ImportProgressBanner() {
    const { isRunning: contactImportRunning, progress: contactImportProgress } = useImportProgress();
    const { isRunning: productGrantRunning, progress: productGrantProgress } = useProductGrantProgress();

    // Show import progress banner (takes priority, matching Vue v-if/v-else-if)
    if (contactImportRunning) {
        return (
            <ProgressBanner
                icon={IcUser}
                title='Importing your contacts... They will appear in 2-3 minutes.'
                progress={contactImportProgress}
            />
        );
    }

    // Show product grant progress banner
    if (productGrantRunning) {
        return (
            <ProgressBanner
                icon={IcBox}
                title='Your product is being manually granted...'
                progress={productGrantProgress}
            />
        );
    }

    return null;
}
